import React from 'react';
import {
  Autocomplete, TextField, Checkbox, ListItemText, Box, MenuItem,
} from '@mui/material';
import { useFetch } from 'use-http';
import PropTypes from 'prop-types';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { findTargetObject } from '../common/atoms/dropDown/utils';
import appColors from '../common/app_colors';

const { selectInput } = appColors;

const AdminPostTargetSearch = ({
  isAdmin = false,
  targetListMeta,
  handleVisibility = () => {},
  selectedTargets,
  fetchPanelsAndNetworks,
}) => {
  const [searchText, setSearchText] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const { abort } = useFetch();
  const debouncedSearchText = useDebouncedValue(searchText, 1000);
  const originalTargetListMeta = React.useRef(targetListMeta);
  // eslint-disable-next-line max-len
  const selectedTargetObjects = React.useMemo(() => selectedTargets.map((targetKey) => options.find((option) => option.targetKey === targetKey)).filter(Boolean) || [], [selectedTargets, options]);

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const onChange = (event, value) => {
    const foundTargets = value.map((val) => findTargetObject({
      targetKey: val.targetKey,
      targets: originalTargetListMeta.current,
      groupType: val.targetKey.split('-')[0],
    }));
    return handleVisibility(foundTargets);
  };

  const uniqueOptions = Array.from(new Set(options.map((option) => option.label))).map((label) => options.find((option) => option.label === label));

  React.useEffect(() => {
    if (isAdmin) {
      fetchPanelsAndNetworks({ abort, searchText });
    }
    // NOTE: do not abort on cleanup. StrictMode + dep-change cleanup
    // would race the in-flight fetch and corrupt the parent's state.
    // `fetchPanelsAndNetworks` owns its own abort semantics via the
    // `abort` callback we hand it.
    return undefined;
  }, [debouncedSearchText]);

  React.useEffect(() => {
    if (originalTargetListMeta.current) {
      const meta = originalTargetListMeta.current;
      const combinedOptions = [
        ...meta.groups.map((group) => ({
          label: group.label,
          targetKey: group.targetKey,
          targetType: group.targetType,
        })),
        ...meta.teams.map((team) => ({ label: team.label, targetKey: team.targetKey, targetType: team.targetType })),
        ...meta.visibility,
      ];
      setOptions(combinedOptions);
    }
  }, [originalTargetListMeta.current]);

  if (!isAdmin) {
    return null;
  }

  return (
    <Autocomplete
      freeSolo
      options={uniqueOptions}
      getOptionLabel={(option) => option.label}
      multiple
      onInputChange={handleSearchTextChange}
      onChange={onChange}
      sx={{
        backgroundColor: selectInput.backgroundColor,
      }}
      value={selectedTargetObjects}
      renderInput={(params) => (
        <TextField {...params} placeholder="Enter Panel or Network" variant="outlined" />
      )}
      renderOption={(props, option) => (
        <MenuItem
          key={option.targetKey}
          value={option.targetKey}
          disableRipple
          {...props}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox checked={selectedTargets.includes(option.targetKey)} style={{ marginRight: '8px' }} />
            <ListItemText primary={option.label} />
          </Box>
        </MenuItem>
      )}
    />
  );
};

AdminPostTargetSearch.propTypes = {
  isAdmin: PropTypes.bool,
  targetListMeta: PropTypes.oneOfType([PropTypes.object]).isRequired,
  handleVisibility: PropTypes.func,
  handleAdminTargets: PropTypes.func,
  selectedTargets: PropTypes.arrayOf(PropTypes.string).isRequired,
};


export default AdminPostTargetSearch;
